(function(root, factory) {
  var api = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.SubmissionCartRewards = api;
  }
})(typeof window !== 'undefined' ? window : null, function(window) {
  'use strict';

  var selectors = {
    config: '[data-cart-rewards-config]',
    cartState: '[data-promotion-cart-state]',
    root: '[data-cart-rewards]',
    content: '[data-cart-rewards-content]',
    message: '[data-cart-rewards-message]',
    description: '[data-cart-rewards-description]',
    code: '[data-cart-rewards-code]',
    giftStatus: '[data-cart-rewards-gift-status]',
    giftChooser: '[data-cart-gift-chooser]',
    giftChooserTitle: '[data-cart-gift-chooser-title]',
    giftLabel: '.native-cart-rewards__gift-label',
    giftSelect: '[data-cart-gift-select]',
    giftSubmit: '[data-cart-gift-submit]',
    giftChoiceStatus: '[data-cart-gift-choice-status]',
    giftLineChange: '[data-cart-gift-line-change]',
    giftLinePicker: '[data-cart-gift-line-picker]',
    meter: '[data-cart-rewards-meter]',
    fill: '[data-cart-rewards-fill]',
    school: '[data-cart-rewards-school]',
    markers: '[data-cart-rewards-markers]',
    summary: '[data-cart-rewards-summary]',
    fishTemplate: '[data-cart-reward-fish-template]',
    hookTemplate: '[data-cart-reward-hook-template]',
    signup: '[data-cart-discount-trigger]',
    editorBlockAnchor: '[data-cart-offer-block-id]',
    editorNotice: '[data-cart-editor-notice]',
    editorWarnings: '[data-cart-editor-warnings]',
    announcementHost: '[data-promotion-announcement-host]',
    announcementFallback: '[data-promotion-announcement-fallback]',
    promotionBar: '[data-promotion-bar]',
    promotionMessage: '[data-promotion-message]',
    promotionCode: '[data-promotion-code]',
    promotionLink: '[data-promotion-link]',
    promotionLinkLabel: '[data-promotion-link-label]'
  };

  var catchDuration = 1800;
  var maximumTimerDelay = 2147483000;
  var states = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var previewOfferId = null;
  var runtimeCartState = null;
  var scheduleTimer = null;
  var giftSyncInFlight = false;
  var giftSyncQueued = false;
  var giftSyncTimer = null;
  var giftSyncError = '';
  var rebuyCartRefreshTimer = null;
  var rebuyCartRefreshGeneration = 0;
  var rebuyCartRefreshInFlight = false;
  var rebuyCartRefreshTrailing = false;
  var lastRenderedCartSignature = '';
  var rebuySmartCartActive = false;
  var cartRevisionGate = createRevisionGate();
  var blockedGiftPromotionIds = {};
  var selectedGiftVariantIds = {};

  function finiteInteger(value) {
    var number = Number(value);
    return Number.isFinite(number) ? Math.round(number) : 0;
  }

  function finiteNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function createRevisionGate(initialRevision) {
    var revision = Math.max(0, finiteInteger(initialRevision));

    return {
      capture: function() {
        return revision;
      },
      invalidate: function() {
        revision += 1;
        return revision;
      },
      isCurrent: function(expectedRevision) {
        return expectedRevision === revision;
      }
    };
  }

  function normalizedText(value) {
    return String(value || '').trim();
  }

  function smartCartIsEnabled() {
    return rebuySmartCartActive || !!(
      window && window.document && window.document.body &&
      window.document.body.classList.contains('smart-cart--enabled')
    );
  }

  function rebuyOwnsGiftAutomation(config, smartCartActive) {
    return normalizedText(config && config.giftAutomationOwner).toLowerCase() === 'rebuy' ||
      smartCartActive === true || smartCartIsEnabled();
  }

  function isRebuyGiftPromotionId(promotionId) {
    return normalizedText(promotionId).indexOf('rebuy:') === 0;
  }

  function cartStateSignature(rawCart) {
    if (!rawCart || typeof rawCart !== 'object') return '';
    var source = rawCart.cart && typeof rawCart.cart === 'object' ? rawCart.cart : rawCart;
    var items = Array.isArray(source.items) ? source.items : [];
    var subtotal = source.cartTotal;
    if (subtotal === undefined || subtotal === null) subtotal = source.items_subtotal_price;
    if (subtotal === undefined || subtotal === null) subtotal = source.total_price;
    var itemCount = source.itemCount;
    if (itemCount === undefined || itemCount === null) itemCount = source.item_count;

    return JSON.stringify({
      subtotal: Math.max(0, finiteInteger(subtotal)),
      itemCount: Math.max(0, finiteInteger(itemCount)),
      items: items.map(function(item) {
        var variantId = item.variantId;
        if (variantId === undefined || variantId === null) variantId = item.variant_id;
        if (variantId === undefined || variantId === null) variantId = item.id;
        var lineTotal = item.lineTotal;
        if (lineTotal === undefined || lineTotal === null) lineTotal = item.final_line_price;
        return [
          normalizedText(item.key),
          normalizedText(variantId),
          Math.max(0, finiteInteger(item.quantity)),
          Math.max(0, finiteInteger(lineTotal))
        ];
      })
    });
  }

  function normalizeCartState(rawState) {
    var source = rawState && typeof rawState === 'object' ? rawState : {};
    var items = (Array.isArray(source.items) ? source.items : []).map(function(rawItem) {
      var item = rawItem && typeof rawItem === 'object' ? rawItem : {};
      return {
        key: normalizedText(item.key),
        variantId: normalizedText(item.variantId),
        quantity: Math.max(0, finiteInteger(item.quantity)),
        giftPromotionId: normalizedText(item.giftPromotionId),
        lineTotal: Math.max(0, finiteInteger(item.lineTotal)),
        preorder: item.preorder === true,
        sale: item.sale === true,
        tags: (Array.isArray(item.tags) ? item.tags : []).map(function(tag) {
          return normalizedText(tag).toLowerCase();
        }).filter(Boolean)
      };
    });

    return {
      cartTotal: Math.max(0, finiteInteger(source.cartTotal)),
      currency: normalizedText(source.currency),
      itemCount: Math.max(0, finiteInteger(source.itemCount)),
      items: items
    };
  }

  function itemHasTag(item, tag) {
    var target = normalizedText(tag).toLowerCase();
    return !!target && item.tags.indexOf(target) !== -1;
  }

  function isPromotionEligible(promotion, rawCartState) {
    var cartState = normalizeCartState(rawCartState);
    var items = cartState.items.filter(function(item) {
      return !item.giftPromotionId;
    });
    var requiredTag = normalizedText(promotion && promotion.requiredTag).toLowerCase();
    var excludedTag = normalizedText(promotion && promotion.excludedTag).toLowerCase();

    if (promotion && promotion.includePreorder === false && items.some(function(item) { return item.preorder; })) {
      return false;
    }

    if (promotion && promotion.includeSale === false && items.some(function(item) { return item.sale; })) {
      return false;
    }

    if (requiredTag && !items.some(function(item) { return itemHasTag(item, requiredTag); })) {
      return false;
    }

    if (excludedTag && items.some(function(item) { return itemHasTag(item, excludedTag); })) {
      return false;
    }

    return true;
  }

  function parseIsoDate(rawValue) {
    var value = normalizedText(rawValue);
    if (!value) {
      return {blank: true, valid: true, timestamp: null, value: ''};
    }

    var match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:\d{2})$/i);
    if (!match) {
      return {blank: false, valid: false, timestamp: null, value: value};
    }

    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var hour = Number(match[4]);
    var minute = Number(match[5]);
    var second = Number(match[6] || 0);
    var daysInMonth = month >= 1 && month <= 12
      ? new Date(Date.UTC(year, month, 0)).getUTCDate()
      : 0;
    var offset = match[8];
    var offsetHour = offset === 'Z' || offset === 'z' ? 0 : Number(offset.slice(1, 3));
    var offsetMinute = offset === 'Z' || offset === 'z' ? 0 : Number(offset.slice(4, 6));

    if (
      month < 1 || month > 12 || day < 1 || day > daysInMonth ||
      hour > 23 || minute > 59 || second > 59 ||
      offsetHour > 14 || offsetMinute > 59 || (offsetHour === 14 && offsetMinute !== 0)
    ) {
      return {blank: false, valid: false, timestamp: null, value: value};
    }

    var timestamp = Date.parse(value);
    return {
      blank: false,
      valid: Number.isFinite(timestamp),
      timestamp: Number.isFinite(timestamp) ? timestamp : null,
      value: value
    };
  }

  function evaluateSchedule(startValue, endValue, rawNow) {
    var now = Number.isFinite(Number(rawNow)) ? Number(rawNow) : Date.now();
    var start = parseIsoDate(startValue);
    var end = parseIsoDate(endValue);
    var valid = start.valid && end.valid;
    var reason = '';

    if (!start.valid) {
      reason = 'has an invalid start date. Use ISO 8601 with an explicit UTC offset.';
    } else if (!end.valid) {
      reason = 'has an invalid end date. Use ISO 8601 with an explicit UTC offset.';
    } else if (start.timestamp !== null && end.timestamp !== null && end.timestamp <= start.timestamp) {
      valid = false;
      reason = 'has an end date that must be later than its start date.';
    }

    var active = valid &&
      (start.timestamp === null || now >= start.timestamp) &&
      (end.timestamp === null || now < end.timestamp);
    var boundaries = [];
    if (valid && start.timestamp !== null && start.timestamp > now) boundaries.push(start.timestamp);
    if (valid && end.timestamp !== null && end.timestamp > now) boundaries.push(end.timestamp);

    return {
      active: active,
      valid: valid,
      reason: reason,
      start: start,
      end: end,
      nextBoundary: boundaries.length ? Math.min.apply(Math, boundaries) : null
    };
  }

  function normalizePromotion(rawPromotion, index, cartState, now) {
    var raw = rawPromotion && typeof rawPromotion === 'object' ? rawPromotion : {};
    var warnings = [];
    var title = normalizedText(raw.title);
    var internalLabel = normalizedText(raw.internalLabel) || title || ('Promotion ' + (index + 1));
    var threshold = finiteInteger(raw.threshold);
    var secondThreshold = finiteInteger(raw.secondThreshold);
    var secondReward = normalizedText(raw.secondReward);
    var secondGiftChoices = (Array.isArray(raw.secondGiftChoices) ? raw.secondGiftChoices : []).map(function(rawChoice) {
      var choice = rawChoice && typeof rawChoice === 'object' ? rawChoice : {};
      return {
        variantId: normalizedText(choice.variantId),
        label: normalizedText(choice.label),
        image: choice.image ? String(choice.image) : null
      };
    }).filter(function(choice, choiceIndex, choices) {
      return /^\d+$/.test(choice.variantId) && choice.label &&
        choices.findIndex(function(candidate) { return candidate.variantId === choice.variantId; }) === choiceIndex;
    });
    var schedule = evaluateSchedule(raw.startAt, raw.endAt, now);
    var allowedTypes = ['percentage', 'fixed', 'free_shipping', 'gift', 'custom'];
    var type = allowedTypes.indexOf(raw.type) !== -1 ? raw.type : 'custom';
    var discountValue = finiteNumber(raw.discountValue);

    if (!title) {
      title = 'Promotion ' + (index + 1);
      warnings.push(internalLabel + ' needs a promotion title; customers will see "' + title + '".');
    }

    if (threshold < 0) {
      warnings.push(internalLabel + ' has a negative threshold. Its cart milestone is hidden.');
      threshold = 0;
    }

    if (secondThreshold > 0 && !secondReward) {
      warnings.push(internalLabel + ' needs a second milestone reward. The second milestone is hidden.');
      secondThreshold = 0;
    } else if (secondThreshold > 0 && secondThreshold <= threshold) {
      warnings.push(internalLabel + ' needs a second milestone above its first threshold. The second milestone is hidden.');
      secondThreshold = 0;
    }

    if (!schedule.valid) {
      warnings.push(internalLabel + ' ' + schedule.reason);
    }

    if (raw.secondGiftChoice === true && secondThreshold > 0 && !secondGiftChoices.length) {
      warnings.push(internalLabel + ' has no in-stock second-gift choices right now.');
    }

    if (type === 'percentage' && (discountValue <= 0 || discountValue > 100)) {
      warnings.push(internalLabel + ' needs a percentage between 0 and 100 for its {discount} placeholder.');
    }

    if (type === 'fixed' && discountValue <= 0) {
      warnings.push(internalLabel + ' needs a positive fixed amount for its {discount} placeholder.');
    }

    var giftVariantId = normalizedText(raw.giftVariantId);
    var autoAddGift = type === 'gift' && raw.autoAddGift === true;
    if (autoAddGift && threshold <= 0) {
      warnings.push(internalLabel + ' needs a positive cart threshold before its gift can be added automatically.');
      autoAddGift = false;
    } else if (autoAddGift && !/^\d+$/.test(giftVariantId)) {
      warnings.push(internalLabel + ' needs a numeric gift variant ID before its gift can be added automatically.');
      autoAddGift = false;
    }

    var promotion = {
      id: String(raw.id || ('promotion-' + (index + 1))),
      internalLabel: internalLabel,
      title: title,
      startAt: normalizedText(raw.startAt),
      endAt: normalizedText(raw.endAt),
      discountCode: normalizedText(raw.discountCode),
      type: type,
      discountValue: discountValue,
      priority: Math.max(1, Math.min(100, finiteInteger(raw.priority) || 50)),
      threshold: threshold,
      secondThreshold: Math.max(0, secondThreshold),
      secondReward: secondReward,
      secondGiftChoice: raw.secondGiftChoice === true,
      secondGiftRemove: raw.secondGiftRemove !== false,
      secondGiftChoices: secondGiftChoices,
      autoAddGift: autoAddGift,
      giftVariantId: giftVariantId,
      removeGiftBelow: raw.removeGiftBelow !== false,
      includePreorder: raw.includePreorder !== false,
      includeSale: raw.includeSale !== false,
      requiredTag: normalizedText(raw.requiredTag),
      excludedTag: normalizedText(raw.excludedTag),
      announcementBefore: String(raw.announcementBefore || 'Add {amount_remaining} more to unlock {discount}.'),
      announcementAfter: String(raw.announcementAfter || '{discount} unlocked.'),
      cartMessage: String(raw.cartMessage || raw.remainingMessage || 'Add {amount_remaining} more to unlock {discount}.'),
      unlockedMessage: String(raw.unlockedMessage || '{discount} unlocked.'),
      showCode: raw.showCode === true,
      showAnnouncement: raw.showAnnouncement === true,
      showCart: raw.showCart !== false,
      showProduct: raw.showProduct === true,
      link: normalizedText(raw.link),
      linkLabel: normalizedText(raw.linkLabel),
      description: normalizedText(raw.description),
      fishImage: raw.fishImage ? String(raw.fishImage) : null,
      order: index,
      schedule: schedule,
      active: schedule.active,
      eligible: false
    };

    promotion.eligible = isPromotionEligible(promotion, cartState);
    return {promotion: promotion, warnings: warnings};
  }

  function normalizePromotions(rawPromotions, rawCartState, rawNow) {
    var cartState = normalizeCartState(rawCartState);
    var warnings = [];
    var promotions = [];
    var nextBoundary = null;

    (Array.isArray(rawPromotions) ? rawPromotions : []).forEach(function(rawPromotion, index) {
      if (!rawPromotion || rawPromotion.enabled === false) return;
      var result = normalizePromotion(rawPromotion, index, cartState, rawNow);
      promotions.push(result.promotion);
      warnings = warnings.concat(result.warnings);
      if (
        result.promotion.schedule.nextBoundary !== null &&
        (nextBoundary === null || result.promotion.schedule.nextBoundary < nextBoundary)
      ) {
        nextBoundary = result.promotion.schedule.nextBoundary;
      }
    });

    return {
      promotions: promotions,
      warnings: warnings,
      nextBoundary: nextBoundary,
      cartState: cartState
    };
  }

  function formatMoney(cents) {
    if (
      window && window.theme && window.theme.Currency &&
      typeof window.theme.Currency.formatMoney === 'function'
    ) {
      return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat)
        .replace(/<[^>]*>/g, '');
    }

    return (Math.max(0, cents) / 100).toFixed(2);
  }

  function compactNumber(value) {
    return String(Math.round(value * 100) / 100);
  }

  function promotionDiscount(promotion) {
    if (promotion.type === 'percentage' && promotion.discountValue > 0 && promotion.discountValue <= 100) {
      return compactNumber(promotion.discountValue) + '% off';
    }
    if (promotion.type === 'fixed' && promotion.discountValue > 0) {
      return formatMoney(Math.round(promotion.discountValue * 100)) + ' off';
    }
    if (promotion.type === 'free_shipping') {
      return promotion.title || 'Free shipping';
    }
    return promotion.title;
  }

  function promotionMilestones(promotion) {
    var milestones = [{
      id: promotion.id + ':1',
      promotionId: promotion.id,
      title: promotion.title,
      discount: promotionDiscount(promotion),
      threshold: promotion.threshold,
      remainingMessage: promotion.cartMessage,
      unlockedMessage: promotion.unlockedMessage,
      description: promotion.description,
      fishImage: promotion.fishImage,
      discountCode: promotion.discountCode,
      showCode: promotion.showCode,
      priority: promotion.priority,
      order: promotion.order,
      milestoneOrder: 0
    }];

    if (promotion.secondThreshold > 0 && promotion.secondReward) {
      milestones.push({
        id: promotion.id + ':2',
        promotionId: promotion.id,
        title: promotion.secondReward,
        discount: promotion.secondReward,
        threshold: promotion.secondThreshold,
        remainingMessage: promotion.cartMessage,
        unlockedMessage: promotion.unlockedMessage,
        description: promotion.description,
        fishImage: promotion.fishImage,
        discountCode: promotion.discountCode,
        showCode: promotion.showCode,
        priority: promotion.priority,
        order: promotion.order,
        milestoneOrder: 1
      });
    }

    return milestones;
  }

  function flattenOffers(promotions) {
    var offers = [];
    (Array.isArray(promotions) ? promotions : []).forEach(function(promotion) {
      if (!promotion.active || !promotion.eligible || !promotion.showCart) return;
      offers = offers.concat(promotionMilestones(promotion).filter(function(milestone) {
        return milestone.threshold > 0;
      }));
    });

    return offers.sort(function(left, right) {
      return left.threshold - right.threshold ||
        left.priority - right.priority ||
        left.order - right.order ||
        left.milestoneOrder - right.milestoneOrder;
    });
  }

  function cartProgressTotal(rawCartState) {
    var cartState = normalizeCartState(rawCartState);
    var managedGiftTotal = cartState.items.reduce(function(total, item) {
      return total + (item.giftPromotionId ? item.lineTotal : 0);
    }, 0);
    return Math.max(0, cartState.cartTotal - managedGiftTotal);
  }

  function createChoiceGiftPromotions(rawPromotions, rawCartState, selectedVariants, blockedIds) {
    var promotions = Array.isArray(rawPromotions) ? rawPromotions : [];
    var cartState = normalizeCartState(rawCartState);
    var selections = selectedVariants || {};
    var blocked = blockedIds || {};

    return promotions.filter(function(promotion) {
      return promotion.secondGiftChoice && promotion.secondThreshold > 0 && promotion.secondReward;
    }).map(function(promotion) {
      var id = promotion.id + ':second-gift';
      var existingLine = cartState.items.find(function(item) {
        return item.giftPromotionId === id;
      });
      var explicitSelection = normalizedText(selections[id]);
      var selectedVariantId = explicitSelection || (existingLine ? existingLine.variantId : '') ||
        (promotion.secondGiftChoices[0] ? promotion.secondGiftChoices[0].variantId : '');
      var selectedChoice = promotion.secondGiftChoices.find(function(choice) {
        return choice.variantId === selectedVariantId;
      }) || null;

      return Object.assign({}, promotion, {
        id: id,
        parentPromotionId: promotion.id,
        title: promotion.secondReward,
        type: 'gift',
        threshold: promotion.secondThreshold,
        secondThreshold: 0,
        secondReward: '',
        autoAddGift: !!selectedChoice && !blocked[id],
        giftVariantId: selectedChoice ? selectedChoice.variantId : '',
        removeGiftBelow: promotion.secondGiftRemove,
        giftChoices: promotion.secondGiftChoices,
        selectedVariantId: selectedChoice ? selectedChoice.variantId : '',
        autoSelected: !!(selectedChoice && !explicitSelection && !existingLine && !blocked[id]),
        choiceVisible: promotion.showCart !== false,
        showCart: false
      });
    });
  }

  function buildGiftPlan(rawPromotions, rawCartState, systemEnabled, blockedIds) {
    var promotions = Array.isArray(rawPromotions) ? rawPromotions : [];
    var cartState = normalizeCartState(rawCartState);
    var progressTotal = cartProgressTotal(cartState);
    var promotionById = {};
    var changes = [];
    var additions = [];
    var blocked = blockedIds || {};

    promotions.forEach(function(promotion) {
      promotionById[promotion.id] = promotion;
    });

    var linesByPromotion = {};
    cartState.items.forEach(function(item) {
      if (!item.giftPromotionId) return;
      // Rebuy owns these lines. They still stay out of the qualifying subtotal,
      // but this theme must never change or remove them.
      if (isRebuyGiftPromotionId(item.giftPromotionId)) return;
      if (!linesByPromotion[item.giftPromotionId]) linesByPromotion[item.giftPromotionId] = [];
      linesByPromotion[item.giftPromotionId].push(item);
    });

    Object.keys(linesByPromotion).forEach(function(promotionId) {
      var promotion = promotionById[promotionId];
      var lines = linesByPromotion[promotionId];
      var shouldHaveGift = !!(
        systemEnabled !== false && promotion && promotion.type === 'gift' &&
        promotion.autoAddGift && promotion.active && promotion.eligible &&
        progressTotal >= promotion.threshold
      );

      if (!shouldHaveGift) {
        if (!promotion || promotion.removeGiftBelow !== false || systemEnabled === false) {
          lines.forEach(function(line) {
            if (line.key) changes.push({key: line.key, quantity: 0, promotionId: promotionId});
          });
        }
        return;
      }

      var keptLine = null;
      lines.forEach(function(line) {
        if (!keptLine && line.variantId === promotion.giftVariantId) {
          keptLine = line;
          if (line.quantity !== 1 && line.key) {
            changes.push({key: line.key, quantity: 1, promotionId: promotionId});
          }
        } else if (line.key) {
          changes.push({key: line.key, quantity: 0, promotionId: promotionId});
        }
      });

      if (!keptLine && !blocked[promotionId]) {
        additions.push({
          variantId: promotion.giftVariantId,
          promotionId: promotionId,
          title: promotion.title
        });
      }
    });

    promotions.forEach(function(promotion) {
      if (linesByPromotion[promotion.id] || blocked[promotion.id]) return;
      if (
        systemEnabled !== false && promotion.type === 'gift' && promotion.autoAddGift &&
        promotion.active && promotion.eligible && progressTotal >= promotion.threshold
      ) {
        additions.push({
          variantId: promotion.giftVariantId,
          promotionId: promotion.id,
          title: promotion.title
        });
      }
    });

    return {
      additions: additions,
      changes: changes,
      progressTotal: progressTotal
    };
  }

  function selectAnnouncement(promotions, location) {
    var productLocation = location === 'product';
    return (Array.isArray(promotions) ? promotions : []).filter(function(promotion) {
      return promotion.active && promotion.eligible &&
        (productLocation ? promotion.showProduct : promotion.showAnnouncement);
    }).sort(function(left, right) {
      return left.priority - right.priority || left.order - right.order;
    })[0] || null;
  }

  function normalizeOffers(rawOffers) {
    var warnings = [];
    var enabled = [];
    var thresholds = Object.create(null);
    var previousThreshold = null;

    (Array.isArray(rawOffers) ? rawOffers : []).forEach(function(rawOffer, index) {
      if (!rawOffer || rawOffer.enabled === false) return;

      var threshold = finiteInteger(rawOffer.threshold);
      var title = normalizedText(rawOffer.title);
      var id = String(rawOffer.id || ('offer-' + (index + 1)));

      if (threshold < 0) {
        warnings.push('Offer ' + (index + 1) + ' is hidden because its threshold cannot be negative.');
        return;
      }

      if (!title) {
        title = 'Reward ' + (index + 1);
        warnings.push('Offer ' + (index + 1) + ' needs a name; customers will see "' + title + '".');
      }

      if (thresholds[threshold]) {
        warnings.push('"' + title + '" shares a threshold with "' + thresholds[threshold] + '". Both offers will unlock together.');
      } else {
        thresholds[threshold] = title;
      }

      if (previousThreshold !== null && threshold < previousThreshold) {
        warnings.push('"' + title + '" has a lower threshold than the offer before it. Merchant order is preserved.');
      }
      previousThreshold = threshold;

      enabled.push({
        id: id,
        promotionId: String(rawOffer.promotionId || id),
        title: title,
        discount: normalizedText(rawOffer.discount) || title,
        threshold: threshold,
        remainingMessage: String(rawOffer.remainingMessage || 'Add {amount_remaining} more to unlock {discount}.'),
        unlockedMessage: String(rawOffer.unlockedMessage || '{discount} unlocked.'),
        description: normalizedText(rawOffer.description),
        fishImage: rawOffer.fishImage ? String(rawOffer.fishImage) : null,
        discountCode: normalizedText(rawOffer.discountCode),
        showCode: rawOffer.showCode === true,
        priority: Math.max(1, Math.min(100, finiteInteger(rawOffer.priority) || 50)),
        order: index
      });
    });

    return {offers: enabled, warnings: warnings};
  }

  function offerSignature(offers) {
    return offers.map(function(offer) {
      return [
        offer.id,
        offer.threshold,
        offer.title,
        offer.discount,
        offer.remainingMessage,
        offer.unlockedMessage,
        offer.discountCode,
        offer.showCode,
        offer.priority,
        offer.fishImage || ''
      ].join(':');
    }).join('|');
  }

  function initialQueue(offers) {
    return {
      visibleIds: offers.slice(0, 3).map(function(offer) { return offer.id; }),
      nextIndex: Math.min(3, offers.length)
    };
  }

  function advanceQueue(offers, state, cartValue) {
    var offerById = Object.create(null);
    offers.forEach(function(offer) { offerById[offer.id] = offer; });

    state.visibleIds = state.visibleIds.filter(function(id) {
      var offer = offerById[id];
      return offer && cartValue < offer.threshold;
    });

    while (state.visibleIds.length < 3 && state.nextIndex < offers.length) {
      var nextOffer = offers[state.nextIndex];
      state.nextIndex += 1;
      state.visibleIds.push(nextOffer.id);
    }

    return state;
  }

  function computeView(config, rawOffers, cartValue, state, selectedOfferId) {
    var normalized = normalizeOffers(rawOffers);
    var offers = normalized.offers;
    var signature = offerSignature(offers);
    var value = Math.max(0, finiteInteger(cartValue));
    var shouldReset = !state || state.signature !== signature || value < state.value;

    if (shouldReset) {
      var queue = initialQueue(offers);
      state = {
        signature: signature,
        value: value,
        previousValue: value,
        visibleIds: offers.length <= 3 ? offers.map(function(offer) { return offer.id; }) : queue.visibleIds,
        nextIndex: offers.length <= 3 ? offers.length : queue.nextIndex,
        announcementOfferId: null,
        timer: null,
        messageTimer: null
      };
    }

    var newlyCompleted = offers.filter(function(offer) {
      return state.previousValue < offer.threshold && value >= offer.threshold;
    });

    if (newlyCompleted.length) {
      newlyCompleted.sort(function(left, right) {
        return right.threshold - left.threshold || left.priority - right.priority || left.order - right.order;
      });
      state.announcementOfferId = newlyCompleted[0].id;
    }

    state.value = value;
    state.previousValue = value;

    var offerById = Object.create(null);
    offers.forEach(function(offer) { offerById[offer.id] = offer; });
    var visibleOffers = state.visibleIds.map(function(id) { return offerById[id]; }).filter(Boolean);
    var selectedOffer = offerById[selectedOfferId] || offers.find(function(offer) {
      return offer.promotionId === selectedOfferId;
    });

    if (selectedOffer && !visibleOffers.some(function(offer) { return offer.id === selectedOffer.id; })) {
      visibleOffers = visibleOffers.slice(0, 2).concat(selectedOffer);
    }

    var completed = offers.filter(function(offer) { return value >= offer.threshold; });
    var nextOffer = offers.find(function(offer) { return value < offer.threshold; }) || null;
    var maxThreshold = offers.reduce(function(maximum, offer) {
      return Math.max(maximum, offer.threshold);
    }, 0);

    return {
      enabled: config && config.enabled !== false,
      offers: offers,
      warnings: normalized.warnings,
      state: state,
      value: value,
      visibleOffers: visibleOffers,
      completed: completed,
      nextOffer: nextOffer,
      maxThreshold: maxThreshold,
      progress: maxThreshold > 0 ? Math.min(100, value * 100 / maxThreshold) : 0,
      announcementOffer: state.announcementOfferId ? offerById[state.announcementOfferId] : null
    };
  }

  function tokenName(braceToken, legacyToken) {
    if (braceToken) return braceToken;
    if (legacyToken === 'amount') return 'amount_remaining';
    if (legacyToken === 'offer') return 'discount';
    return legacyToken;
  }

  function replaceTokens(template, tokens) {
    return String(template || '').replace(
      /\{(amount_remaining|cart_total|threshold|discount|discount_code)\}|\[(amount|offer)\]/g,
      function(match, braceToken, legacyToken) {
        var name = tokenName(braceToken, legacyToken);
        if (Object.prototype.hasOwnProperty.call(tokens, name)) return tokens[name];
        if (legacyToken === 'amount' && Object.prototype.hasOwnProperty.call(tokens, 'amount')) return tokens.amount;
        if (legacyToken === 'offer' && Object.prototype.hasOwnProperty.call(tokens, 'offer')) return tokens.offer;
        return match;
      }
    );
  }

  function setMessage(element, template, tokens) {
    if (!element || typeof document === 'undefined') return;
    element.textContent = '';

    var source = String(template || '');
    var pattern = /\{(amount_remaining|cart_total|threshold|discount|discount_code)\}|\[(amount|offer)\]/g;
    var cursor = 0;
    var match;

    while ((match = pattern.exec(source))) {
      element.appendChild(document.createTextNode(source.slice(cursor, match.index)));
      var name = tokenName(match[1], match[2]);
      var value = Object.prototype.hasOwnProperty.call(tokens, name) ? tokens[name] : match[0];
      if (match[2] === 'amount' && Object.prototype.hasOwnProperty.call(tokens, 'amount')) value = tokens.amount;
      if (match[2] === 'offer' && Object.prototype.hasOwnProperty.call(tokens, 'offer')) value = tokens.offer;
      if (['amount_remaining', 'cart_total', 'threshold', 'discount_code'].indexOf(name) !== -1) {
        var strong = document.createElement('strong');
        strong.textContent = value;
        element.appendChild(strong);
      } else {
        element.appendChild(document.createTextNode(value));
      }
      cursor = pattern.lastIndex;
    }

    element.appendChild(document.createTextNode(source.slice(cursor)));
  }

  function tokensForOffer(offer, cartValue) {
    return {
      amount_remaining: formatMoney(Math.max(offer.threshold - cartValue, 0)),
      cart_total: formatMoney(cartValue),
      threshold: formatMoney(offer.threshold),
      discount: offer.discount || offer.title,
      discount_code: offer.showCode ? offer.discountCode : ''
    };
  }

  function tokensForPromotion(promotion, milestone, cartValue) {
    return {
      amount_remaining: formatMoney(Math.max(milestone.threshold - cartValue, 0)),
      cart_total: formatMoney(cartValue),
      threshold: formatMoney(milestone.threshold),
      discount: milestone.discount,
      discount_code: promotion.showCode ? promotion.discountCode : ''
    };
  }

  function hasCodeToken(template) {
    return /\{discount_code\}/.test(String(template || ''));
  }

  function cloneTemplate(rootElement, selector) {
    var template = rootElement.querySelector(selector);
    if (!template || !template.content || !template.content.firstElementChild) return null;
    return template.content.firstElementChild.cloneNode(true);
  }

  function createFish(rootElement, offer) {
    if (offer.fishImage) {
      var image = document.createElement('img');
      image.className = 'native-cart-rewards__fish native-cart-rewards__fish--image';
      image.src = offer.fishImage;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 28;
      image.height = 16;
      return image;
    }

    return cloneTemplate(rootElement, selectors.fishTemplate);
  }

  function renderEditorWarnings(warnings) {
    if (typeof document === 'undefined') return;
    var notice = document.querySelector(selectors.editorNotice);
    var list = notice && notice.querySelector(selectors.editorWarnings);
    if (!notice || !list) return;

    list.textContent = '';
    warnings.forEach(function(warning) {
      var item = document.createElement('li');
      item.textContent = warning;
      list.appendChild(item);
    });
    notice.hidden = warnings.length === 0;
  }

  function uniqueWarnings(warnings) {
    return warnings.filter(function(warning, index, allWarnings) {
      return allWarnings.indexOf(warning) === index;
    });
  }

  function parseJsonElement(element) {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  function parseConfig() {
    if (typeof document === 'undefined') return null;
    return parseJsonElement(document.querySelector(selectors.config));
  }

  function applyEditorBlockOwnership(element, blockId) {
    if (!element || typeof document === 'undefined') return;

    Array.prototype.slice.call(element.attributes || []).forEach(function(attribute) {
      if (attribute.name.indexOf('data-shopify-editor-') === 0) {
        element.removeAttribute(attribute.name);
      }
    });
    delete element.dataset.cartOfferBlockId;

    var normalizedBlockId = normalizedText(blockId);
    if (!normalizedBlockId || typeof document.querySelectorAll !== 'function') return;
    var anchors = Array.prototype.slice.call(document.querySelectorAll(selectors.editorBlockAnchor));
    var anchor = anchors.find(function(candidate) {
      return candidate.getAttribute('data-cart-offer-block-id') === normalizedBlockId;
    });
    if (!anchor) return;

    Array.prototype.slice.call(anchor.attributes || []).forEach(function(attribute) {
      if (attribute.name.indexOf('data-shopify-editor-') === 0) {
        element.setAttribute(attribute.name, attribute.value);
      }
    });
    element.dataset.cartOfferBlockId = normalizedBlockId;
  }

  function parseCartState(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return null;
    var elements = scope.querySelectorAll(selectors.cartState);
    var element = elements.length ? elements[elements.length - 1] : null;
    var parsed = parseJsonElement(element);
    return parsed ? normalizeCartState(parsed) : null;
  }

  function clearStateTimer(state, key) {
    if (!state || !state[key] || !window) return;
    window.clearTimeout(state[key]);
    state[key] = null;
  }

  function renderGiftChooser(rootElement, choicePromotions, cartState, cartValue) {
    var chooser = rootElement.querySelector(selectors.giftChooser);
    if (!chooser) return false;

    var availablePromotions = (Array.isArray(choicePromotions) ? choicePromotions : []).filter(function(promotion) {
      return promotion.active && promotion.eligible && promotion.choiceVisible && cartValue >= promotion.threshold;
    }).sort(function(left, right) {
      return left.priority - right.priority || left.order - right.order;
    });
    var promotion = availablePromotions[0] || null;

    if (!promotion) {
      chooser.hidden = true;
      delete chooser.dataset.giftPromotionId;
      applyEditorBlockOwnership(chooser, '');
      return false;
    }

    var title = chooser.querySelector(selectors.giftChooserTitle);
    var label = chooser.querySelector(selectors.giftLabel);
    var select = chooser.querySelector(selectors.giftSelect);
    var submit = chooser.querySelector(selectors.giftSubmit);
    var status = chooser.querySelector(selectors.giftChoiceStatus);
    var existingLine = cartState.items.find(function(item) {
      return item.giftPromotionId === promotion.id;
    });
    var selectedVariantId = existingLine ? existingLine.variantId : promotion.selectedVariantId;

    chooser.dataset.giftPromotionId = promotion.id;
    applyEditorBlockOwnership(chooser, promotion.parentPromotionId || promotion.id);
    chooser.dataset.currentVariantId = existingLine ? existingLine.variantId : '';
    chooser.hidden = chooser.dataset.open !== 'true';
    if (title) title.textContent = promotion.title + ' unlocked';

    if (select) {
      select.textContent = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = promotion.giftChoices.length
        ? 'Select an in-stock hook option'
        : 'No hook options are currently in stock';
      select.appendChild(placeholder);

      promotion.giftChoices.forEach(function(choice) {
        var option = document.createElement('option');
        option.value = choice.variantId;
        option.textContent = choice.label;
        select.appendChild(option);
      });
      select.value = promotion.giftChoices.some(function(choice) {
        return choice.variantId === selectedVariantId;
      }) ? selectedVariantId : '';
      select.disabled = promotion.giftChoices.length === 0;
    }

    var selectedChoice = promotion.giftChoices.find(function(choice) {
      return choice.variantId === (select ? select.value : selectedVariantId);
    });
    var existingMatches = !!(existingLine && selectedChoice && existingLine.variantId === selectedChoice.variantId);
    var autoAdding = !!(!existingLine && promotion.autoSelected && selectedChoice);
    chooser.dataset.mode = existingMatches ? 'summary' : (autoAdding ? 'adding' : 'edit');
    if (label) label.hidden = existingMatches || autoAdding;
    if (submit) {
      submit.textContent = existingMatches
        ? 'Change hooks'
        : (autoAdding ? 'Adding hooks…' : (existingLine ? 'Update hooks' : 'Add hooks'));
      submit.disabled = autoAdding || !selectedChoice;
    }
    if (status) {
      status.textContent = !promotion.giftChoices.length
        ? 'No qualifying hook options are currently in stock.'
        : (existingMatches
          ? 'Selected: ' + selectedChoice.label
          : (autoAdding ? 'Adding: ' + selectedChoice.label : ''));
    }

    return true;
  }

  function renderRoot(rootElement, config, offers, cartValue, choicePromotions, cartState) {
    if (!rootElement || !config) return {warnings: []};
    var existingState = states && states.get(rootElement);
    var view = computeView(config, offers, cartValue, existingState, previewOfferId);
    var state = view.state;

    if (states) states.set(rootElement, state);

    var content = rootElement.querySelector(selectors.content);
    var hasSignup = Array.from(rootElement.querySelectorAll(selectors.signup)).some(function(trigger) {
      return !trigger.hidden;
    });
    var hasOffers = view.enabled && view.offers.length > 0;
    renderGiftChooser(rootElement, choicePromotions, cartState, cartValue);
    rootElement.classList.remove('is-loading');
    rootElement.classList.toggle('has-no-offers', !hasOffers);
    rootElement.hidden = !hasOffers && !hasSignup;
    if (content) content.hidden = !hasOffers;

    if (!hasOffers) {
      clearStateTimer(state, 'timer');
      clearStateTimer(state, 'messageTimer');
      return {warnings: view.warnings};
    }

    var message = rootElement.querySelector(selectors.message);
    var description = rootElement.querySelector(selectors.description);
    var code = rootElement.querySelector(selectors.code);
    var messageTemplate;
    var messageOffer;
    var pendingChoice = (Array.isArray(choicePromotions) ? choicePromotions : []).find(function(promotion) {
      var alreadySelected = cartState.items.some(function(item) {
        return item.giftPromotionId === promotion.id;
      });
      return promotion.active && promotion.eligible && promotion.choiceVisible &&
        cartValue >= promotion.threshold && !alreadySelected;
    });

    if (view.announcementOffer) {
      messageOffer = view.announcementOffer;
      messageTemplate = messageOffer.unlockedMessage;
    } else if (view.nextOffer) {
      messageOffer = view.nextOffer;
      messageTemplate = messageOffer.remainingMessage;
    } else {
      messageOffer = view.offers[view.offers.length - 1] || null;
      messageTemplate = view.offers.length === 1
        ? messageOffer.unlockedMessage
        : (config.allUnlockedMessage || 'All rewards unlocked');
    }

    if (pendingChoice && message) {
      message.textContent = pendingChoice.giftChoices.length
        ? (pendingChoice.autoSelected
          ? pendingChoice.title + ' unlocked — adding an in-stock option.'
          : pendingChoice.title + ' unlocked — choose yours below.')
        : pendingChoice.title + ' unlocked, but no options are currently in stock.';
    } else {
      setMessage(message, messageTemplate, messageOffer
        ? tokensForOffer(messageOffer, view.value)
        : {amount_remaining: '', cart_total: formatMoney(view.value), threshold: '', discount: '', discount_code: ''});
    }

    var messagePromotionId = pendingChoice
      ? (pendingChoice.parentPromotionId || pendingChoice.id)
      : (messageOffer && messageOffer.promotionId);
    applyEditorBlockOwnership(message, messagePromotionId);
    applyEditorBlockOwnership(description, messagePromotionId);
    applyEditorBlockOwnership(code, messagePromotionId);

    if (description) {
      description.textContent = view.nextOffer ? view.nextOffer.description : '';
      description.hidden = !description.textContent;
    }

    if (code) {
      var showCode = messageOffer && messageOffer.showCode && messageOffer.discountCode && !hasCodeToken(messageTemplate);
      code.textContent = showCode ? 'Code: ' + messageOffer.discountCode : '';
      code.hidden = !showCode;
    }

    var giftStatus = rootElement.querySelector(selectors.giftStatus);
    if (giftStatus) {
      giftStatus.textContent = giftSyncError;
      giftStatus.hidden = !giftSyncError;
    }

    var meter = rootElement.querySelector(selectors.meter);
    var fill = rootElement.querySelector(selectors.fill);
    var showMeter = config.showProgress !== false && view.maxThreshold > 0;
    if (meter) {
      meter.hidden = !showMeter;
      meter.setAttribute('aria-label', config.progressLabel || 'Cart rewards progress');
      meter.setAttribute('aria-valuemax', view.maxThreshold);
      meter.setAttribute('aria-valuenow', Math.min(view.value, view.maxThreshold));
      meter.setAttribute('aria-valuetext', message ? message.textContent.trim() : '');
      meter.style.setProperty('--reward-progress', view.progress + '%');
    }
    if (fill) fill.style.width = view.progress + '%';

    var school = rootElement.querySelector(selectors.school);
    var markers = rootElement.querySelector(selectors.markers);
    if (school) school.textContent = '';
    if (markers) markers.textContent = '';

    view.visibleOffers.filter(function(offer) { return offer.threshold > 0; }).forEach(function(offer) {
      var isCompleted = view.value >= offer.threshold;
      var position = view.maxThreshold > 0
        ? Math.max(4, Math.min(98, offer.threshold * 100 / view.maxThreshold))
        : 0;
      var marker = document.createElement('span');
      marker.className = 'native-cart-rewards__marker' + (isCompleted ? ' is-unlocked' : '');
      marker.style.setProperty('--reward-position', position + '%');
      marker.dataset.rewardOfferId = offer.id;
      applyEditorBlockOwnership(marker, offer.promotionId);
      marker.title = offer.title + ' - ' + formatMoney(offer.threshold);

      var hook = cloneTemplate(rootElement, selectors.hookTemplate);
      if (hook) marker.appendChild(hook);
      if (isCompleted) {
        var caughtFish = createFish(rootElement, offer);
        if (caughtFish) marker.appendChild(caughtFish);
      } else if (school) {
        var swimmingFish = createFish(rootElement, offer);
        if (swimmingFish) school.appendChild(swimmingFish);
      }
      if (markers) markers.appendChild(marker);
    });
    if (school) school.hidden = school.childElementCount === 0;

    var summary = rootElement.querySelector(selectors.summary);
    if (summary) {
      summary.textContent = view.offers.map(function(offer) {
        var status = view.value >= offer.threshold
          ? replaceTokens(offer.unlockedMessage, tokensForOffer(offer, view.value))
          : formatMoney(Math.max(offer.threshold - view.value, 0)) + ' until ' + offer.title;
        return formatMoney(offer.threshold) + ': ' + status;
      }).join('. ');
    }

    var completedVisible = view.visibleOffers.filter(function(offer) {
      return view.value >= offer.threshold;
    });

    if (view.offers.length > 3 && completedVisible.length && !state.timer && window) {
      state.timer = window.setTimeout(function() {
        state.timer = null;
        advanceQueue(view.offers, state, state.value);
        renderAll();
      }, catchDuration);
    }

    if (state.announcementOfferId && !state.messageTimer && window) {
      state.messageTimer = window.setTimeout(function() {
        state.messageTimer = null;
        state.announcementOfferId = null;
        renderAll();
      }, catchDuration);
    }

    return {warnings: view.warnings};
  }

  function safePromotionLink(rawLink) {
    var link = normalizedText(rawLink);
    if (!link) return '';
    if (/^(https?:|mailto:|tel:)/i.test(link) || /^\/(?!\/)/.test(link) || /^#/.test(link)) return link;
    return '';
  }

  function renderAnnouncementHost(host, promotions, cartState) {
    var bar = host.querySelector(selectors.promotionBar);
    var fallback = host.querySelector(selectors.announcementFallback);
    var hasFallback = host.dataset.promotionHasFallback === 'true';
    var location = bar ? bar.dataset.promotionLocation : 'announcement';
    var promotion = selectAnnouncement(promotions, location);
    applyEditorBlockOwnership(bar, promotion && promotion.id);

    if (!bar || !promotion) {
      if (bar) bar.hidden = true;
      if (fallback) fallback.hidden = !hasFallback;
      host.hidden = !hasFallback;
      delete host.dataset.activePromotionId;
      return;
    }

    var milestones = promotionMilestones(promotion).sort(function(left, right) {
      return left.threshold - right.threshold;
    });
    var nextMilestone = milestones.find(function(milestone) {
      return cartState.cartTotal < milestone.threshold;
    }) || null;
    var messageMilestone = nextMilestone || milestones[milestones.length - 1];
    var messageTemplate = nextMilestone ? promotion.announcementBefore : promotion.announcementAfter;
    var message = bar.querySelector(selectors.promotionMessage);
    var code = bar.querySelector(selectors.promotionCode);
    var link = bar.querySelector(selectors.promotionLink);
    var linkLabel = link && link.querySelector(selectors.promotionLinkLabel);

    setMessage(message, messageTemplate, tokensForPromotion(promotion, messageMilestone, cartState.cartTotal));

    var showCode = promotion.showCode && promotion.discountCode && !hasCodeToken(messageTemplate);
    if (code) {
      code.textContent = showCode ? 'Code: ' + promotion.discountCode : '';
      code.hidden = !showCode;
    }

    var promotionLink = safePromotionLink(promotion.link);
    var showLink = !!(promotionLink && promotion.linkLabel);
    if (link) {
      if (showLink) {
        link.setAttribute('href', promotionLink);
      } else {
        link.removeAttribute('href');
      }
      link.hidden = !showLink;
    }
    if (linkLabel) linkLabel.textContent = showLink ? promotion.linkLabel : '';

    var hasContent = !!(
      (message && message.textContent.trim()) ||
      showCode ||
      showLink
    );
    if (!hasContent) {
      bar.hidden = true;
      if (fallback) fallback.hidden = !hasFallback;
      host.hidden = !hasFallback;
      return;
    }

    bar.hidden = false;
    if (fallback) fallback.hidden = true;
    host.hidden = false;
    host.dataset.activePromotionId = promotion.id;
  }

  function scheduleNextRender(nextBoundary) {
    if (!window) return;
    if (scheduleTimer) {
      window.clearTimeout(scheduleTimer);
      scheduleTimer = null;
    }
    if (!Number.isFinite(nextBoundary)) return;

    var delay = Math.max(25, nextBoundary - Date.now() + 25);
    scheduleTimer = window.setTimeout(function() {
      scheduleTimer = null;
      renderAll();
    }, Math.min(delay, maximumTimerDelay));
  }

  function cartEndpoint(path) {
    var rootPath = window && window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return rootPath.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  function cartRequest(path, options) {
    return window.fetch(cartEndpoint(path), Object.assign({
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }, options || {})).then(function(response) {
      return response.text().then(function(text) {
        var payload = null;
        try {
          payload = text ? JSON.parse(text) : null;
        } catch (error) {
          payload = null;
        }
        if (!response.ok) {
          throw new Error((payload && (payload.description || payload.message)) || 'Shopify could not update the promotional gift.');
        }
        return payload;
      });
    });
  }

  function executeGiftPlan(plan) {
    var chain = Promise.resolve();

    if (plan.additions.length) {
      chain = chain.then(function() {
        // Section Store's PDP bundle patches fetch globally and otherwise appends
        // the currently selected product pack to unrelated cart/add requests.
        return cartRequest('cart/add.js?ss_bundle_skip=1', {
          method: 'POST',
          body: JSON.stringify({
            items: plan.additions.map(function(addition) {
              return {
                id: Number(addition.variantId),
                quantity: 1,
                properties: {
                  _promotion_gift: addition.promotionId,
                  _promotion_gift_label: addition.title
                }
              };
            })
          })
        });
      });
    }

    plan.changes.forEach(function(change) {
      chain = chain.then(function() {
        return cartRequest('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({id: change.key, quantity: change.quantity})
        });
      });
    });

    return chain;
  }

  function setGiftSyncPending(pending) {
    if (typeof document === 'undefined' || typeof document.dispatchEvent !== 'function') return;
    document.dispatchEvent(new CustomEvent('cart:gift-sync', {
      detail: {pending: pending === true}
    }));
  }

  function requestCartBuild(source) {
    if (typeof document === 'undefined' || typeof document.dispatchEvent !== 'function') {
      return Promise.resolve();
    }

    var refreshes = [];
    document.dispatchEvent(new CustomEvent('cart:build', {
      detail: {
        source: normalizedText(source) || 'promotion-gift',
        waitUntil: function(refresh) {
          if (refresh && typeof refresh.then === 'function') {
            refreshes.push(refresh);
          }
        }
      }
    }));

    return refreshes.length ? Promise.all(refreshes) : Promise.resolve();
  }

  function enqueueGiftMutation(operation) {
    var cartApi = window && window.theme && window.theme.cart;
    return cartApi && typeof cartApi.enqueueMutation === 'function'
      ? cartApi.enqueueMutation('promotion-gift', operation)
      : Promise.resolve().then(operation);
  }

  function invalidatePendingGiftPlans(event) {
    var kind = event && event.detail ? normalizedText(event.detail.kind) : '';
    if (kind === 'promotion-gift') return;

    cartRevisionGate.invalidate();
    if (giftSyncTimer) {
      window.clearTimeout(giftSyncTimer);
      giftSyncTimer = null;
    }
    if (giftSyncInFlight) giftSyncQueued = true;
  }

  function syncAutomaticGifts(config, promotions, cartState, expectedRevision) {
    if (!window || !window.fetch) return;
    if (!cartRevisionGate.isCurrent(expectedRevision)) return;
    if (giftSyncInFlight) {
      giftSyncQueued = true;
      return;
    }

    var themeAutomationEnabled = config.enabled !== false && !rebuyOwnsGiftAutomation(config);
    var plan = buildGiftPlan(promotions, cartState, themeAutomationEnabled, blockedGiftPromotionIds);
    if (!plan.additions.length && !plan.changes.length) return;

    giftSyncInFlight = true;
    giftSyncQueued = false;
    setGiftSyncPending(true);

    enqueueGiftMutation(function() {
      if (!cartRevisionGate.isCurrent(expectedRevision)) {
        return {stale: true};
      }

      return executeGiftPlan(plan).then(function() {
        return requestCartBuild();
      }).then(function() {
        return {stale: false};
      });
    }).then(function(result) {
      if (result && result.stale) return;
      giftSyncError = '';
    }).catch(function(error) {
      plan.additions.forEach(function(addition) {
        blockedGiftPromotionIds[addition.promotionId] = true;
      });
      giftSyncError = error && error.message
        ? error.message
        : 'The promotional gift could not be added. Please refresh the cart.';
      if (plan.additions.length) {
        requestCartBuild();
      }
      renderAll();
    }).finally(function() {
      giftSyncInFlight = false;
      setGiftSyncPending(false);
      if (giftSyncQueued) {
        giftSyncQueued = false;
        window.setTimeout(renderAll, 0);
      }
    });
  }

  function scheduleAutomaticGiftSync(config, promotions, cartState) {
    if (!window) return;
    if (giftSyncTimer) window.clearTimeout(giftSyncTimer);
    var expectedRevision = cartRevisionGate.capture();

    giftSyncTimer = window.setTimeout(function() {
      giftSyncTimer = null;
      syncAutomaticGifts(config, promotions, cartState, expectedRevision);
    }, 120);
  }

  function runRebuyCartRefresh() {
    if (rebuyCartRefreshInFlight) {
      rebuyCartRefreshTrailing = true;
      return;
    }

    rebuyCartRefreshInFlight = true;
    rebuyCartRefreshTrailing = false;
    var expectedGeneration = rebuyCartRefreshGeneration;

    requestCartBuild('rebuy-gift').catch(function(error) {
      if (window.console && typeof window.console.error === 'function') {
        window.console.error('Unable to refresh the cart after a Rebuy update.', error);
      }
    }).finally(function() {
      rebuyCartRefreshInFlight = false;
      if (rebuyCartRefreshTrailing || expectedGeneration !== rebuyCartRefreshGeneration) {
        rebuyCartRefreshTrailing = false;
        runRebuyCartRefresh();
        return;
      }
      setGiftSyncPending(false);
    });
  }

  function onRebuyCartChange(event) {
    var config = parseConfig();
    if (!rebuyOwnsGiftAutomation(config) || !window) return;

    var eventCart = event && event.detail && event.detail.cart;
    var eventSignature = cartStateSignature(eventCart);
    if (eventSignature && eventSignature === lastRenderedCartSignature) return;

    invalidatePendingGiftPlans();
    rebuyCartRefreshGeneration += 1;

    if (rebuyCartRefreshTimer) window.clearTimeout(rebuyCartRefreshTimer);
    setGiftSyncPending(true);

    if (rebuyCartRefreshInFlight) {
      rebuyCartRefreshTrailing = true;
      return;
    }

    // Two tiered widgets can update in quick succession. Coalescing their
    // events avoids rendering an intermediate one-gift cart.
    rebuyCartRefreshTimer = window.setTimeout(function() {
      rebuyCartRefreshTimer = null;
      runRebuyCartRefresh();
    }, 75);
  }

  function onRebuySmartCartLifecycle(event) {
    rebuySmartCartActive = true;
    invalidatePendingGiftPlans();
    renderAll();

    if (event && event.type === 'rebuy:smartcart.ready') {
      onRebuyCartChange(event);
    }
  }

  function renderAll() {
    if (typeof document === 'undefined') return;
    var config = parseConfig();
    if (!config) return;

    var cartState = runtimeCartState || normalizeCartState(config.cartState);
    lastRenderedCartSignature = cartStateSignature(cartState);
    var normalized = normalizePromotions(config.promotions || config.offers, cartState, Date.now());
    var promotions = config.enabled === false ? [] : normalized.promotions;
    var allChoicePromotions = createChoiceGiftPromotions(
      normalized.promotions,
      cartState,
      selectedGiftVariantIds,
      blockedGiftPromotionIds
    );
    var choicePromotions = config.enabled === false ? [] : allChoicePromotions;
    var offers = flattenOffers(promotions);
    var progressTotal = cartProgressTotal(cartState);
    var offerWarnings = normalizeOffers(offers).warnings;
    var rootWarnings = [];

    document.querySelectorAll(selectors.root).forEach(function(rootElement) {
      rootElement.dataset.cartValue = progressTotal;
      var result = renderRoot(rootElement, config, offers, progressTotal, choicePromotions, cartState);
      rootWarnings = rootWarnings.concat(result.warnings || []);
    });

    document.querySelectorAll(selectors.giftLinePicker).forEach(function(picker) {
      var promotionId = normalizedText(picker.dataset.giftPromotionId);
      var promotion = choicePromotions.find(function(candidate) {
        return candidate.id === promotionId;
      });
      var existingLine = cartState.items.find(function(item) {
        return item.giftPromotionId === promotionId;
      });
      if (!promotion) return;

      picker.textContent = '';
      promotion.giftChoices.forEach(function(choice) {
        var option = document.createElement('option');
        option.value = choice.variantId;
        option.textContent = choice.label;
        picker.appendChild(option);
      });
      picker.value = existingLine ? existingLine.variantId : promotion.selectedVariantId;
      picker.disabled = picker.dataset.updating === 'true' || promotion.giftChoices.length < 2;
    });

    document.querySelectorAll(selectors.announcementHost).forEach(function(host) {
      renderAnnouncementHost(host, promotions, Object.assign({}, cartState, {cartTotal: progressTotal}));
    });

    renderEditorWarnings(uniqueWarnings(normalized.warnings.concat(offerWarnings, rootWarnings)));
    scheduleNextRender(config.enabled === false ? null : normalized.nextBoundary);
    scheduleAutomaticGiftSync(config, normalized.promotions.concat(allChoicePromotions), cartState);
  }

  function onGiftChoiceChange(event) {
    var linePicker = event.target && event.target.closest ? event.target.closest(selectors.giftLinePicker) : null;
    if (linePicker) {
      var linePromotionId = normalizedText(linePicker.dataset.giftPromotionId);
      var lineVariantId = normalizedText(linePicker.value);
      if (!linePromotionId || !/^\d+$/.test(lineVariantId)) return;
      selectedGiftVariantIds[linePromotionId] = lineVariantId;
      delete blockedGiftPromotionIds[linePromotionId];
      giftSyncError = '';
      linePicker.dataset.updating = 'true';
      var lineControl = linePicker.closest('.cart__promotion-gift-change-control');
      if (lineControl) {
        lineControl.classList.add('is-updating');
        lineControl.setAttribute('aria-busy', 'true');
      }
      linePicker.disabled = true;
      renderAll();
      return;
    }

    var select = event.target && event.target.closest ? event.target.closest(selectors.giftSelect) : null;
    if (!select) return;
    var chooser = select.closest(selectors.giftChooser);
    var submit = chooser && chooser.querySelector(selectors.giftSubmit);
    var status = chooser && chooser.querySelector(selectors.giftChoiceStatus);
    var currentVariantId = chooser ? normalizedText(chooser.dataset.currentVariantId) : '';
    if (submit) submit.disabled = !select.value || select.value === currentVariantId;
    if (status && select.value !== currentVariantId) status.textContent = '';
  }

  function openGiftChooser(chooser, trigger) {
    if (!chooser) return;
    var label = chooser.querySelector(selectors.giftLabel);
    var select = chooser.querySelector(selectors.giftSelect);
    var submit = chooser.querySelector(selectors.giftSubmit);
    var status = chooser.querySelector(selectors.giftChoiceStatus);
    chooser.dataset.open = 'true';
    chooser.hidden = false;
    chooser.dataset.mode = 'edit';
    if (label) label.hidden = false;
    if (submit) {
      submit.textContent = 'Update hooks';
      submit.disabled = true;
    }
    if (status) status.textContent = 'Choose another in-stock hook option.';
    if (chooser.scrollIntoView) chooser.scrollIntoView({block: 'nearest', behavior: 'auto'});
    if (select) select.focus();
    if (trigger) chooser.dataset.returnFocus = 'true';
  }

  function onGiftChoiceSubmit(event) {
    var lineChange = event.target && event.target.closest ? event.target.closest(selectors.giftLineChange) : null;
    if (lineChange) {
      var linePromotionId = normalizedText(lineChange.dataset.giftPromotionId);
      var lineRoot = lineChange.closest('form') || document;
      var lineChooser = Array.from(lineRoot.querySelectorAll(selectors.giftChooser)).find(function(candidate) {
        return normalizedText(candidate.dataset.giftPromotionId) === linePromotionId;
      });
      openGiftChooser(lineChooser, lineChange);
      return;
    }

    var submit = event.target && event.target.closest ? event.target.closest(selectors.giftSubmit) : null;
    if (!submit) return;
    var chooser = submit.closest(selectors.giftChooser);
    if (chooser && chooser.dataset.mode === 'summary') {
      openGiftChooser(chooser, submit);
      return;
    }
    var select = chooser && chooser.querySelector(selectors.giftSelect);
    var status = chooser && chooser.querySelector(selectors.giftChoiceStatus);
    var promotionId = chooser && normalizedText(chooser.dataset.giftPromotionId);
    var variantId = select && normalizedText(select.value);
    if (!promotionId || !/^\d+$/.test(variantId)) return;

    selectedGiftVariantIds[promotionId] = variantId;
    delete blockedGiftPromotionIds[promotionId];
    giftSyncError = '';
    submit.disabled = true;
    if (status) status.textContent = 'Adding your free hooks…';
    renderAll();
  }

  function onCartRendered(event) {
    var detail = event.detail || {};
    var scope = detail.root || document;
    var state = parseCartState(scope);

    cartRevisionGate.invalidate();

    if (state) {
      runtimeCartState = state;
    } else {
      var current = runtimeCartState || normalizeCartState({});
      runtimeCartState = normalizeCartState({
        cartTotal: Number.isFinite(Number(detail.progressSubtotal)) ? detail.progressSubtotal : detail.subtotal,
        currency: current.currency,
        itemCount: detail.count,
        items: current.items
      });
    }

    renderAll();
  }

  function init() {
    if (!window || typeof document === 'undefined') return;
    renderAll();
    document.addEventListener('cart:rendered', onCartRendered);
    document.addEventListener('rebuy:cart.ready', onRebuyCartChange);
    document.addEventListener('rebuy:cart.change', onRebuyCartChange);
    document.addEventListener('rebuy:smartcart.init', onRebuySmartCartLifecycle);
    document.addEventListener('rebuy:smartcart.ready', onRebuySmartCartLifecycle);
    document.addEventListener('rebuy:smartcart.show', onRebuySmartCartLifecycle);
    document.addEventListener('cart:mutation:queued', invalidatePendingGiftPlans);
    document.addEventListener('ajaxProduct:added', invalidatePendingGiftPlans);
    document.addEventListener('cart-rewards:refresh', renderAll);
    document.addEventListener('change', onGiftChoiceChange);
    document.addEventListener('click', onGiftChoiceSubmit);
    document.addEventListener('shopify:section:load', function() {
      window.setTimeout(renderAll, 0);
    });
    document.addEventListener('shopify:block:select', function(event) {
      previewOfferId = event.detail && event.detail.blockId ? String(event.detail.blockId) : null;
      renderAll();
    });
    document.addEventListener('shopify:block:deselect', function() {
      previewOfferId = null;
      renderAll();
    });
  }

  if (window && window.document) {
    if (window.document.readyState === 'loading') {
      window.document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  return {
    advanceQueue: advanceQueue,
    applyEditorBlockOwnership: applyEditorBlockOwnership,
    buildGiftPlan: buildGiftPlan,
    cartStateSignature: cartStateSignature,
    cartProgressTotal: cartProgressTotal,
    computeView: computeView,
    createRevisionGate: createRevisionGate,
    createChoiceGiftPromotions: createChoiceGiftPromotions,
    evaluateSchedule: evaluateSchedule,
    flattenOffers: flattenOffers,
    initialQueue: initialQueue,
    isPromotionEligible: isPromotionEligible,
    isRebuyGiftPromotionId: isRebuyGiftPromotionId,
    normalizeCartState: normalizeCartState,
    normalizeOffers: normalizeOffers,
    normalizePromotions: normalizePromotions,
    parseIsoDate: parseIsoDate,
    promotionMilestones: promotionMilestones,
    rebuyOwnsGiftAutomation: rebuyOwnsGiftAutomation,
    replaceTokens: replaceTokens,
    selectAnnouncement: selectAnnouncement
  };
});
